import React, { useState, useMemo } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { History } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, Kpi } from "@/components/common";
import { inrL, num } from "@/lib/format";

const RANGES = [[6, "6h"], [24, "24h"], [72, "3d"], [168, "7d"]];
const METRICS = [
  ["utility_per_l", "Utility ₹/L"], ["margin_l", "Margin ₹/L"],
  ["cop", "Refrigeration COP"], ["boiler_eff", "Boiler Eff %"],
  ["carbon_l", "Carbon kgCO₂/L"], ["production_klpd", "Production KLPD"],
];

export default function HistoryReplay() {
  const [hours, setHours] = useState(24);
  const [metric, setMetric] = useState("utility_per_l");
  const [idx, setIdx] = useState(null);
  const { data: replay } = usePoll(() => endpoints.replay(hours), 0, [hours]);
  const { data: hist } = usePoll(() => endpoints.history(hours, metric), 0, [hours, metric]);

  const frames = replay?.frames || [];
  const curIdx = idx == null && frames.length ? frames.length - 1 : idx;
  const frame = frames[curIdx];

  const series = useMemo(() => (hist?.series || []).map((d) => ({
    ...d, tnum: new Date(d.ts).getTime(),
  })), [hist]);
  const fmtTime = (v) => new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="history-replay-page">
      <PageHeader icon={History} title="History & Replay"
        subtitle="Persisted 7-day time-series — scrub back in time and replay the dairy at any instant."
        right={
          <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="range-toggle">
            {RANGES.map(([h, label]) => (
              <button key={h} onClick={() => { setHours(h); setIdx(null); }} data-testid={`range-${h}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${hours === h ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                {label}
              </button>
            ))}
          </div>
        } />

      <Panel testid="replay-scrubber" title="Replay Scrubber" subtitle={frame ? new Date(frame.ts).toLocaleString("en-IN") : "—"} className="mb-4">
        {frames.length === 0 ? <Loading label="Loading history…" /> : (
          <>
            <input type="range" min={0} max={frames.length - 1} value={curIdx} data-testid="replay-slider"
              onChange={(e) => setIdx(parseInt(e.target.value))}
              className="w-full accent-slate-900 mb-4" />
            {frame && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi testid="replay-production" label="Production" value={num(frame.ribbons.production.milk_klpd)} unit="KLPD" accent="#2563eb" />
                <Kpi testid="replay-utility" label="Utility Cost" value={inrL(frame.ribbons.economics.utility_per_l)} unit="/L" accent="#10b981" />
                <Kpi testid="replay-margin" label="Margin" value={inrL(frame.ribbons.economics.margin_per_l)} unit="/L" />
                <Kpi testid="replay-carbon" label="Carbon" value={frame.ribbons.sustainability.carbon_intensity} unit="kgCO₂/L" accent="#059669" />
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel testid="history-trend" title="Long-Range Trend" subtitle={`Persisted from MongoDB · ${hours}h`}
        right={
          <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="history-metric-select"
            className="text-xs font-semibold border border-slate-200 rounded px-2 py-1 focus:outline-none">
            {METRICS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        }>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="tnum" type="number" scale="time" domain={["dataMin", "dataMax"]}
              tickFormatter={fmtTime} tick={{ fontSize: 9 }} minTickGap={70} />
            <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
            <Tooltip labelFormatter={fmtTime} />
            <Line type="linear" dataKey={metric} stroke="#2563eb" strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}
